import { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadHandler } from './use-upload-handler';

/**
 * Empty-state landing surface: large drop zone in the main content
 * area when no dataset is loaded yet. Delegates ingest lifecycle
 * (worker call, profile, dashboardName rehydration, error reporting,
 * storage refresh) to {@link useUploadHandler}.
 *
 * Falling back: a click on the zone opens the native file picker, so
 * the experience also works on inputs without drag support.
 */
export function UploadDropZone(): JSX.Element {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { busy, blocked, handleFile } = useUploadHandler();
  const [dragOver, setDragOver] = useState(false);

  async function processList(list: FileList | null): Promise<void> {
    const file = list?.item(0) ?? null;
    await handleFile(file);
    if (inputRef.current) inputRef.current.value = '';
  }

  function handleDragOver(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  }

  function handleDragLeave(e: DragEvent<HTMLDivElement>): void {
    // Only clear when the drag actually leaves the zone (not when it
    // moves over child elements).
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDragOver(false);
  }

  function handleDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setDragOver(false);
    void processList(e.dataTransfer.files);
  }

  const borderColor = dragOver
    ? 'var(--color-accent)'
    : 'var(--color-border)';
  const bg = dragOver
    ? 'color-mix(in srgb, var(--color-accent) 8%, transparent)'
    : 'transparent';

  return (
    <section
      className="flex items-center justify-center"
      style={{ minHeight: '60vh' }}
    >
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !busy && !blocked && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!busy && !blocked) inputRef.current?.click();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={t('upload.dropZone.aria')}
        className="flex flex-col items-center justify-center gap-3 px-10 py-12 rounded-2xl transition-colors cursor-pointer focus:outline-none focus:ring-2"
        style={{
          minWidth: '320px',
          maxWidth: '520px',
          border: `2px dashed ${borderColor}`,
          background: bg,
          color: 'var(--color-fg)',
          opacity: blocked ? 0.6 : 1,
        }}
      >
        {/* Cloud / arrow glyph */}
        <svg
          width="48"
          height="48"
          viewBox="0 0 48 48"
          fill="none"
          aria-hidden
          style={{ color: 'var(--color-accent)' }}
        >
          <path
            d="M24 30V14m0 0l-7 7m7-7l7 7"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10 32a6 6 0 0 1 1.5-11.85A10 10 0 0 1 32 18.2 7 7 0 0 1 36 32H10z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>

        <div className="text-base font-semibold text-center">
          {busy
            ? t('upload.loading')
            : blocked
              ? t('upload.blockedTooltip')
              : t('upload.dropZone.primary')}
        </div>
        <div
          className="text-xs text-center"
          style={{ color: 'var(--color-muted)' }}
        >
          {t('upload.dropZone.secondary')}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={(e) => void processList(e.target.files)}
          className="hidden"
          aria-hidden
        />
      </div>
    </section>
  );
}
